import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bell, BellRing, RefreshCw, X } from 'lucide-react';
import { fetchJSON } from '../lib/api-client';
import { currentNotificationPermission, requestNotificationPermission, showBrowserNotification, type Alert } from '../lib/notifications';

interface PendingAlertsResponse { alerts: Alert[]; count: number }
type ToastAlert = Alert & { shownAt: number };

const TOAST_TTL_MS = 18_000;

function isTabVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

function permissionLabel(permission: NotificationPermission): string {
  if (permission === 'granted') return '通知已開';
  if (permission === 'denied') return '通知被封鎖';
  return '啟用企鵝通知';
}

function severityClass(severity: Alert['severity']): string {
  if (severity === 'critical') return 'border-(--down)/60 bg-(--down)/15';
  if (severity === 'warning') return 'border-(--warn)/60 bg-(--warn)/15';
  return 'border-(--accent)/50 bg-(--accent)/8';
}

async function markAlertRead(id: string): Promise<void> {
  await fetchJSON(`/api/alerts/${encodeURIComponent(id)}/read`, { method: 'POST' });
}

export function NotificationCenter() {
  const handledRef = useRef<Set<string>>(new Set());
  const [permission, setPermission] = useState<NotificationPermission>(() => currentNotificationPermission());
  const [visible, setVisible] = useState(() => isTabVisible());
  const [toasts, setToasts] = useState<ToastAlert[]>([]);

  useEffect(() => {
    const onVisibilityChange = () => setVisible(isTabVisible());
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timer = window.setInterval(() => {
      const cutoff = Date.now() - TOAST_TTL_MS;
      setToasts((prev) => prev.filter((toast) => toast.shownAt >= cutoff));
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [toasts.length]);

  const pending = useQuery({
    queryKey: ['alerts', 'pending'],
    queryFn: () => fetchJSON<PendingAlertsResponse>('/api/alerts/pending'),
    enabled: visible,
    refetchInterval: visible ? 60_000 : false,
    staleTime: 15_000,
    retry: false,
  });

  useEffect(() => {
    if (visible) pending.refetch().catch(() => { /* best effort */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    const alerts = pending.data?.alerts ?? [];
    if (alerts.length === 0) return;
    for (const alert of alerts) {
      if (handledRef.current.has(alert.id)) continue;
      handledRef.current.add(alert.id);
      if (permission === 'granted') {
        showBrowserNotification(alert);
      } else {
        setToasts((prev) => [
          { ...alert, shownAt: Date.now() },
          ...prev.filter((item) => item.id !== alert.id),
        ].slice(0, 5));
      }
      markAlertRead(alert.id).catch(() => handledRef.current.delete(alert.id));
    }
  }, [pending.data?.alerts, permission]);

  async function enableNotifications() {
    const next = await requestNotificationPermission();
    setPermission(next);
    pending.refetch().catch(() => { /* best effort */ });
  }

  const ButtonIcon = permission === 'granted' ? BellRing : Bell;

  return (
    <>
      <button
        type="button"
        onClick={enableNotifications}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs border ${permission === 'granted' ? 'text-(--accent) border-(--accent)/40 bg-(--accent)/10' : 'text-(--text-muted) border-(--border) bg-(--bg-soft) hover:border-(--accent) hover:text-(--accent)'}`}
        title="開著分頁時，企鵝會用瀏覽器通知或頁面內提醒告訴你停損 / T1 / T2 / 熔斷事件。"
      >
        <ButtonIcon size={13} />
        <span>{permissionLabel(permission)}</span>
        {pending.isFetching && <RefreshCw size={11} className="animate-spin" />}
      </button>

      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 w-[min(360px,calc(100vw-2rem))] space-y-2">
          {toasts.map((toast) => (
            <div key={toast.id} className={`rounded-lg border p-3 shadow-xl backdrop-blur-md ${severityClass(toast.severity)}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-(--text-primary)">{toast.title}</div>
                  <div className="mt-1 text-[12px] leading-relaxed text-(--text-secondary)">{toast.body}</div>
                  <div className="mt-1 text-[10px] text-(--text-muted) tnum">
                    {toast.symbol} · {new Date(toast.createdAt).toLocaleString('zh-TW', { hour12: false })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setToasts((prev) => prev.filter((item) => item.id !== toast.id))}
                  className="shrink-0 rounded border border-(--border) p-1 text-(--text-muted) hover:border-(--accent) hover:text-(--accent)"
                  aria-label="關閉通知"
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}